// /home/bigbrain/projects/smashparty/src/client/ui/screens/onboarding.ts
/**
 * Plan 24a, Task 2.8 — Onboarding Tutorial screen (Screen 33)
 *
 * Animated gameplay instruction sequence. The warmup-first flow mounts this
 * when a player enters their first real round.
 *
 * Behaviour:
 *   - Renders a sequence of 7 (or 8 with FEATURES branch) scripted text banners.
 *   - Space keydown OR a click on the banner advances to the next step.
 *   - Advancing past the final step marks completion and calls `onComplete`;
 *     without a callback it returns to the warmup arena.
 *   - SKIP button (top-right, Ghost / Small) immediately completes via the same
 *     flag-set + navigation path.
 *
 * FEATURES branch (per docs/superpowers/specs/2026-04-26-minigames-feature-flag-design.md §4.7):
 *   - Evaluated ONCE at mount; the resulting steps array is immutable for the
 *     screen lifetime.
 *   - When `FEATURES.MINIGAMES_ENABLED && FEATURES.MINIGAMES_AVAILABLE.includes('animal-count')`
 *     is true → 8-step script (animal-count step included at index 6).
 *   - Otherwise → 7-step script (animal-count step omitted entirely).
 *
 * Banner-component note:
 *   The plan refers to `createTurnBanner` from src/client/ui/components/turn-banner.ts.
 *   That handle's `show(opts)` always appends "'s Turn!" to opts.playerName
 *   (turn-banner.ts:194 `textEl.textContent = `${playerName}'s Turn!``), which
 *   is semantically wrong for tutorial step text — "Press Space/A to roll's Turn!"
 *   would render. This screen uses a self-contained banner renderer that
 *   replicates the visual structure (full-width fixed banner, centered headline
 *   text, GSAP slide-in/hold/slide-out timeline) WITHOUT the suffix.
 *
 * Design system references:
 *   - docs/design/tokens.json — typography.family.headline, typography.size.turn-banner,
 *     typography.shadow.title, color.ui.overlay-dim, animation.duration.normal
 *   - docs/design/components.md §18 (Turn Banner)
 *   - docs/design/screens.md — Screen 33 (Onboarding Tutorial)
 *   - docs/superpowers/specs/2026-04-26-minigames-feature-flag-design.md §4.7
 */

import { createButton } from '../components/button';
import type { UIComponentHandle } from '../components/types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';
import { FEATURES } from '../../../shared/config/features';
import type { ScreenHandle, ScreenMountContext } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type OnboardingPayload = {
  /** Called after the sequence completes. Default: return to warmup arena. */
  onComplete?: () => void;
  /** localStorage key to mark on completion. Default: smashparty_onboarded. */
  completionStorageKey?: string | null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Constants — localStorage key + step scripts
// ─────────────────────────────────────────────────────────────────────────────

/** localStorage key tracking whether the user has completed onboarding once. */
const ONBOARDED_KEY = 'smashparty_onboarded';

/** Minigame id whose presence in MINIGAMES_AVAILABLE gates the 8-step path. */
const GATED_MINIGAME_ID = 'animal-count';

/** Common steps shared by both 7- and 8-step variants (steps 1–6). */
const COMMON_STEPS_PREFIX: readonly string[] = [
  'Press Space/A to roll',
  'Moving step-by-step',
  'Landing on Gold — You picked up 5 keys!',
  'Landing on Heal — You healed 15 HP',
  'Landing on Hazard — Hazard! You took 5 damage',
  'Landing on Item tile — using item on a bot',
];

/** Gated step inserted at position 6 (zero-indexed) when FEATURES allow it. */
const ANIMAL_COUNT_STEP = 'Animal Count — playing one short minigame';

/** Final closing step (step 8 in the full script, step 7 in the 7-step variant). */
const FINAL_STEP = "You're ready!";

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — exact values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** typography.family.headline */
const FONT_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";

/** typography.size.turn-banner = 42px (matches Plan 23b Turn Banner) */
const FONT_SIZE = '42px';

/** color.text.primary = #FFFFFF */
const TEXT_COLOR = '#FFFFFF';

/** typography.shadow.title = "0 3px 0 #000000, 0 6px 12px rgba(0,0,0,0.50)" */
const TEXT_SHADOW = '0 3px 0 #000000, 0 6px 12px rgba(0,0,0,0.50)';

/**
 * Banner background — semi-transparent dark panel.
 * Closest token: color.ui.overlay-dim = "rgba(0, 0, 0, 0.60)" — slightly tuned
 * with the panel-bg blue cast for visual weight per components.md §18.
 */
const BANNER_BG = 'rgba(10, 14, 26, 0.90)';

/** animation.duration.normal = 300ms (slide entrance/exit), HOLD = 1500ms. */
const ANIM_DURATION = 0.3;
const HOLD_DURATION = 1.5;
const T_EXIT_START = ANIM_DURATION + HOLD_DURATION; // 1.8s
const T_EXIT_END = T_EXIT_START + ANIM_DURATION; // 2.1s

/** GSAP easing (matches turn-banner) */
const EASE_SNAPPY = 'power2.inOut';

/** z-index: notification tier (matches turn-banner z=500) */
const Z_INDEX = 500;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute the immutable step array based on the current FEATURES flag values.
 * Evaluated ONCE at mount.
 */
function buildSteps(): readonly string[] {
  const includeAnimalCount =
    FEATURES.MINIGAMES_ENABLED && FEATURES.MINIGAMES_AVAILABLE.includes(GATED_MINIGAME_ID);
  if (includeAnimalCount) {
    return [...COMMON_STEPS_PREFIX, ANIMAL_COUNT_STEP, FINAL_STEP];
  }
  return [...COMMON_STEPS_PREFIX, FINAL_STEP];
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createOnboardingScreen(ctx: ScreenMountContext<OnboardingPayload>): ScreenHandle {
  const { gsapRegistry, host, router, payload } = ctx;

  let disposed = false;
  let currentStepIndex = 0;
  let completed = false;

  // Evaluate FEATURES branch ONCE — captured into immutable local.
  const steps = buildSteps();

  /** In-flight banner timeline; killed/replaced on each step transition. */
  let currentTimeline: GsapTimelineHandle | null = null;

  // ── Root element ───────────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-onboarding';
  root.style.cssText = [
    `position: relative`,
    `width: 100%`,
    `height: 100%`,
    `overflow: hidden`,
    `box-sizing: border-box`,
    // Subtle dim background so the banner reads as the focal element.
    `background: var(--color-ui-overlay-dim, rgba(0, 0, 0, 0.60))`,
  ].join('; ');

  // ── Banner element (full-width horizontal, vertically centered) ────────────

  const banner = document.createElement('div');
  banner.className = 'ui-onboarding-banner';
  banner.style.cssText = [
    `position: fixed`,
    `left: 0`,
    `right: 0`,
    `top: 50%`,
    `transform: translateY(-50%)`,
    `min-height: 80px`,
    `padding: 16px 32px`,
    `display: flex`,
    `align-items: center`,
    `justify-content: center`,
    `background: ${BANNER_BG}`,
    `box-shadow: 0 8px 24px rgba(0, 0, 0, 0.50)`,
    `z-index: ${Z_INDEX}`,
    `cursor: pointer`,
    `user-select: none`,
  ].join('; ');

  const bannerText = document.createElement('span');
  bannerText.className = 'ui-onboarding-banner__text';
  bannerText.style.cssText = [
    `font-family: ${FONT_FAMILY}`,
    `font-size: ${FONT_SIZE}`,
    `font-weight: 900`,
    `color: ${TEXT_COLOR}`,
    `text-shadow: ${TEXT_SHADOW}`,
    `text-align: center`,
    `line-height: 1.2`,
    `pointer-events: none`,
  ].join('; ');
  banner.appendChild(bannerText);

  root.appendChild(banner);

  // ── SKIP button (top-right, Ghost / Small) ────────────────────────────────

  const skipBtnHandle: UIComponentHandle = createButton({
    gsapRegistry,
    variant: 'ghost',
    size: 'small',
    label: 'SKIP',
    onClick: handleSkipClick,
  });
  const skipEl = skipBtnHandle.element;
  skipEl.setAttribute('data-onboarding-skip', 'true');
  skipEl.setAttribute('aria-label', 'Skip onboarding');
  skipEl.style.position = 'absolute';
  skipEl.style.top = 'var(--spacing-md, 16px)';
  skipEl.style.right = 'var(--spacing-md, 16px)';
  skipEl.style.zIndex = String(Z_INDEX + 1);
  root.appendChild(skipEl);

  // ── Mount root into host ───────────────────────────────────────────────────

  host.appendChild(root);

  // ── Render the initial banner (step 0) ─────────────────────────────────────

  renderCurrentStep();

  // ── Document-level Space-key listener ──────────────────────────────────────

  document.addEventListener('keydown', handleKeyDown);
  banner.addEventListener('click', handleBannerClick);

  // ─────────────────────────────────────────────────────────────────────────────
  // Step rendering / advancement
  // ─────────────────────────────────────────────────────────────────────────────

  function renderCurrentStep(): void {
    if (disposed) return;
    if (currentStepIndex < 0 || currentStepIndex >= steps.length) return;

    bannerText.textContent = steps[currentStepIndex] as string;

    // Kill any in-flight banner timeline before starting a fresh one.
    if (currentTimeline) {
      currentTimeline.dispose();
      currentTimeline = null;
    }

    // Slide-in / hold / slide-out timeline (visual only — does NOT control
    // step advancement; user-driven via Space/click).
    const handle = gsapRegistry.create(`onboarding:step-${String(currentStepIndex)}`);
    currentTimeline = handle;
    const tl = handle.timeline;

    // F20a-CLASS-1 compliance: every tween/call uses an explicit numeric position.
    tl.fromTo(
      banner,
      { xPercent: -100 },
      { xPercent: 0, duration: ANIM_DURATION, ease: EASE_SNAPPY, overwrite: true },
      0,
    );
    tl.to(banner, { xPercent: 0, duration: HOLD_DURATION, ease: 'none' }, ANIM_DURATION);
    tl.to(banner, { xPercent: 0, duration: ANIM_DURATION, ease: EASE_SNAPPY }, T_EXIT_START);
    // Sentinel call at the end so the timeline has a non-degenerate tail.
    tl.call(() => undefined, undefined, T_EXIT_END);
  }

  function advance(): void {
    if (disposed) return;
    if (completed) return;

    if (currentStepIndex >= steps.length - 1) {
      // Past the final step → complete.
      complete();
      return;
    }

    currentStepIndex += 1;
    renderCurrentStep();
  }

  function complete(): void {
    if (completed) return;
    completed = true;

    try {
      const key =
        payload.completionStorageKey === undefined ? ONBOARDED_KEY : payload.completionStorageKey;
      if (key != null) {
        localStorage.setItem(key, 'true');
      }
    } catch {
      // Best-effort: localStorage may be unavailable in private modes.
    }

    if (payload.onComplete != null) {
      payload.onComplete();
    } else {
      router.navigate('warmup-arena-overlay', { portal: null });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Event handlers
  // ─────────────────────────────────────────────────────────────────────────────

  function handleKeyDown(e: KeyboardEvent): void {
    if (disposed) return;
    // Match either by key (' ') or code ('Space') — happy-dom synthesises both.
    if (e.key === ' ' || e.code === 'Space') {
      advance();
    }
  }

  function handleBannerClick(): void {
    if (disposed) return;
    advance();
  }

  function handleSkipClick(): void {
    if (disposed) return;
    if (completed) return;
    complete();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // dispose
  // ─────────────────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    document.removeEventListener('keydown', handleKeyDown);
    banner.removeEventListener('click', handleBannerClick);

    if (currentTimeline) {
      currentTimeline.dispose();
      currentTimeline = null;
    }

    skipBtnHandle.dispose();

    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { element: root, dispose };
}
